import { IApiErrorResponse } from '@/core/models/api/api.models';
import { Console } from '@/shared/providers/custom-logger';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Servizio per la gestione delle chiamate API HTTP.
 * Fornisce metodi per effettuare richieste GET, POST, PUT e DELETE verso l'API.
 * Gestisce automaticamente la pulizia degli endpoint e la gestione degli errori.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  
  private log_label = 'ApiService';
  readonly BASE_URL = environment.API_ENDPOINT;
  private http = inject(HttpClient);

  /**
   * Pulisce l'endpoint rimuovendo lo slash iniziale se presente
   * @param endpoint - L'endpoint da pulire
   * @returns L'endpoint pulito senza slash iniziale
   */
  private cleanEndpoint(endpoint: string): string {
    return endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  }

  /**
   * Effettua una richiesta HTTP GET
   * @param endpoint - L'endpoint dell'API da chiamare
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  get<T>(endpoint: string): Observable<T> {
    Console.log(`[${this.log_label}] GET Request to:`, `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`, 'info');
    return this.http
      .get<{ data: T }>(`${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`)
      .pipe(
        map((response: any): T => {
          Console.log(`[${this.log_label}] GET Response:`, response, 'info');
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          Console.log(`[${this.log_label}] Error occurred:`, error, 'err');
          return throwError(() => error.error as IApiErrorResponse);
        })
      );
  }

  // Se esiste solo message e non data{}, ritorna message, altrimenti data{}

  /**
   * Effettua una richiesta HTTP POST
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Il corpo della richiesta
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  post<T, B>(endpoint: string, body: B): Observable<T> {
    Console.log(`[${this.log_label}] POST Request to:`, `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`, 'info');
    Console.log(`[${this.log_label}] POST Body:`, body, 'info');
    return this.http
      .post<{ data: T } | T>(
        `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`,
        body
      )
      .pipe(
        map((response: any): T => {
          Console.log(`[${this.log_label}] POST Response:`, response, 'info');
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          Console.log(`[${this.log_label}] Error occurred:`, error, 'err');
          return throwError(() => error.error as IApiErrorResponse);
        })
      );
  }

  /**
   * Effettua una richiesta HTTP PUT
   * @param endpoint - L'endpoint dell'API da chiamare
   * @param body - Il corpo della richiesta
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  put<T, B>(endpoint: string, body: B): Observable<T> {
    Console.log(`[${this.log_label}] PUT Request to:`, `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`, 'info');
    Console.log(`[${this.log_label}] PUT Body:`, body, 'info');
    return this.http
      .put<{ data: T } | T>(
        `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`,
        body
      )
      .pipe(
        map((response: any): T => {
          Console.log(`[${this.log_label}] PUT Response:`, response, 'info');
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          Console.log(`[${this.log_label}] Error occurred:`, error, 'err');
          return throwError(() => error.error as IApiErrorResponse);
        })
      );
  }

  /**
   * Effettua una richiesta HTTP DELETE
   * @param endpoint - L'endpoint dell'API da chiamare
   * @returns Observable contenente la risposta dell'API
   * @throws IApiErrorResponse in caso di errore
   */
  delete<T>(endpoint: string): Observable<T> {
    Console.log(`[${this.log_label}] DELETE Request to:`, `${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`, 'info');
    return this.http
      .delete<{ data: T }>(`${this.BASE_URL}/${this.cleanEndpoint(endpoint)}`)
      .pipe(
        map((response) => {
          Console.log(`[${this.log_label}] DELETE Response:`, response, 'info');
          return response.data;
        }),
        catchError((error: HttpErrorResponse) => {
          Console.log(`[${this.log_label}] Error occurred:`, error, 'err');
          return throwError(() => error.error as IApiErrorResponse);
        })
      );
  }
}
